<template>
  <v-container
    fill-height
    justify-content-start
    :class="{ 'section-width': lgAndUp }"
    class="pa-0"
  >
    <v-layout v-if="extendedActivity" column>
      <div
        flat
        class="activity-header"
        :style="{
          'background-image':
            'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(' +
            extendedActivity.activity.coverImageUrl +
            ')',
        }"
      >
        <div class="activity-title mt-2">
          <v-icon @click="exit()" color="white" class="ms-3 me-3"
            >mdi-arrow-left</v-icon
          >
          <v-toolbar-title>{{ extendedActivity.activity.name }}</v-toolbar-title>
        </div>
        <br />
        <div class="activity-title mt-2" v-if="extendedActivity.reservationMethods.length">
          <span v-for="(reservationMethod, i) in extendedActivity.reservationMethods" :key="i">
            <span>{{ i === 0
              ? $t(`activities.reservation_types.${reservationMethod.reservationMethod.type}`)
              : $t(`activities.reservation_types.${reservationMethod.reservationMethod.type}`).toLowerCase() }}</span>
            <span v-if="(i + 1) !== extendedActivity.reservationMethods.length" class="show-spaces"> & </span>
          </span>
        </div>
        <br />
        <v-layout class="pa-2">
          <v-chip
            label
            v-if="current !== null"
            color="#BBD2D2"
            text-color="#1C4949"
            class="mr-2"
            >{{ $t("activities.details.open") }}</v-chip
          >
          <v-chip label v-else-if="!isInformativeActivity" class="mr-2">{{
            $t("activities.details.closed")
          }}</v-chip>
          <timetable-dialog
            v-if="timetable && xsOnly && !isInformativeActivity"
            :chipText="
              current ? current : $t('activities.details.see_timetable')
            "
            :timetable="timetable"
          ></timetable-dialog>
          <v-chip
            v-if="timetable && current && smAndUp"
            label
          >
            <v-icon left>mdi-calendar</v-icon>
            {{ current }}
          </v-chip>
        </v-layout>
      </div>
      <v-flex class="app-scroll-section pa-2">
        <v-container>
          <v-layout>
            <v-flex xs12 :sm8="!isInformativeActivity">
              <foldable-card-text
                class="ma-1"
                :minLength="380"
                :text="extendedActivity ? extendedActivity.activity.description : ''"
              ></foldable-card-text>
              <image-carousel v-if="extendedActivity.activity.images.length" :images="extendedActivity.activity.images" />
              <v-btn
                @click="downloadCatalogFile()"
                v-if="extendedActivity.activity.catalogFileId"
                outlined
                block
                class="primary-gradient mt-3"
              >
                {{ extendedActivity ? extendedActivity.activity.catalogCallToAction : '' }}
              </v-btn>
            </v-flex>
            <v-flex v-if="smAndUp && !isInformativeActivity" sm4>
              <v-card elevation="0" solid color="grey lighten-3" class="ma-1">
                <v-card-title>{{
                  $t("activities.details.timetable")
                }}</v-card-title>
                <v-card-text>
                  <timetable :timetable="timetable"></timetable>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
          <v-layout v-if="extendedActivity && extendedActivity.activity.relatedActivities && extendedActivity.activity.relatedActivities.length && activeExtendedActivities.length > 0" row wrap>
            <v-flex xs12 class="title secondary--text mt-5">{{$t('activities.details.related_activities')}}</v-flex>
            <v-flex xs12>
              <v-layout row wrap :class="smAndUp ? 'px-12' : 'px-4'">
                <v-flex xs12 sm6 v-for="(relatedActivity, id) in activeExtendedActivities" :key="id">
                  <related-activity :activity="relatedActivity"></related-activity>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>
      </v-flex>
      <v-layout v-if="activeReservationMethods.length && workingHours.length" justify-center class="ma-2 fit-content" row wrap>
        <v-flex xs12 sm6 md5 v-for="(reservationMethod, i) in activeReservationMethods" :key="reservationMethod.id" :class="{ 'pr-1': !i && smAndUp, 'pl-1': i && smAndUp, 'pt-2': i && xsOnly }">
          <reservation-type-button @start-reservation="startReservation(reservationMethod)" :bookings="bookings" :reservationMethod="reservationMethod" />
        </v-flex>
      </v-layout>
    </v-layout>
    <v-layout v-else justify-center align-center>
      <v-progress-circular indeterminate />
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'

import { RequestService } from '@/services/request.service'
import ServerDirections from '@/config/server-directions'
import { reservationTypes } from '@/config'

import WorkingHoursMixins from '@/mixins/WorkingHours'
import VuetifyMixins from '@/mixins/Vuetify'
import routeNames from '@/router/routes'

const requestService = new RequestService()

export default {
  mixins: [
    WorkingHoursMixins,
    VuetifyMixins,
  ],
  data () {
    return {
      routeNames,
      timetable: [],
      current: null,
      workingHours: [],
      isInformativeActivity: true,
    }
  },
  created () {
    moment.locale(this.$i18n.locale)
    this.$store.dispatch('me/getBookings')
    this.$store.commit('cart/resetCart')
    this.$store
      .dispatch('activities/getActivity', { id: this.$route.params.activityId })
      .then(() => {
        this.$store
          .dispatch('activities/getWorkingHours', {
            id: this.$route.params.activityId,
            start: moment().startOf('week').format('YYYY-MM-DD'),
            end: moment().endOf('week').format('YYYY-MM-DD'),
          })
          .then((workingHours) => {
            this.workingHours = workingHours
            this.timetable = this.getWeekTimeTable(workingHours)
            this.getCurrent()
            if (workingHours.length) {
              this.isInformativeActivity = false
            } else {
              this.$store.dispatch('activities/getWorkingHours', {
                id: this.$route.params.activityId,
                start: moment().subtract(1, 'year').format('YYYY-MM-DD'),
                end: moment().add(1, 'year').format('YYYY-MM-DD'),
              }).then((wHs) => {
                this.isInformativeActivity = !wHs.length
              })
            }
          })
      })
  },
  methods: {
    startReservation (reservationMethod) {
      this.$store.commit('activityReservations/reset')
      localStorage.setItem('reservationMethodType', reservationMethod.type)
      this.$router.push({ name: routeNames.reservationCommonData.name })
    },
    exit () {
      this.$router.go(-1)
    },
    async downloadCatalogFile () {
      let fileId = this.extendedActivity.activity.catalogFileId
      const token = await requestService.authHeader().catch((error) => {
        return new Promise((resolve, reject) => {
          reject(error)
        })
      })
      window.open(ServerDirections.PUBLIC_FILES.replace('{fileId}', fileId) + `?x-api-key=${ token }`)
    },
  },
  components: {
    ImageCarousel: () => import('@/components/app-components/ImageCarousel.vue'),
    Timetable: () => import('@/components/view-components/activities/Timetable.vue'),
    FoldableCardText: () => import('@/components/app-components/FoldableCardText.vue'),
    RelatedActivity: () => import('@/components/view-components/activities/RelatedActivity.vue'),
    TimetableDialog: () => import('@/components/view-components/activities/TimetableDialog.vue'),
    ReservationTypeButton: () => import('@/components/view-components/activities/ReservationTypeButton.vue'),
  },
  computed: {
    ...mapGetters('activities', ['activityById']),
    ...mapState({
      activities: state => state.activities,
      extendedActivity: state => state.activities.extendedActivity,
      bookings: state => state.me.bookings,
    }),
    activeReservationMethods () {
      return this.extendedActivity.reservationMethods
        .filter(({ reservationMethod }) => {
          return reservationMethod.active && (reservationMethod.type === reservationTypes.IN_VENUE || this.timetable.some(({ day, hours }) => day === moment().day() && !!hours.length))
        })
        .map(extendedReservationMethod => extendedReservationMethod.reservationMethod)
    },
    reservationMethodType () {
      return this.$store.state.activityReservations.edited.type
    },
    activeExtendedActivities () {
      let activeExtendedActivities = []
      this.extendedActivity.activity.relatedActivities.forEach(element => {
        if (element.active) {
          activeExtendedActivities.push(element)
        }
      })
      return activeExtendedActivities
    },
  },
}
</script>

<style lang="scss" scoped>
.section-width {
  max-width: 800px !important;
}
</style>
